import React, { FC, useContext } from "react";
import { BlockFragment } from "../../graphql-types";
import {
  spaceRem,
  narrowCenterRem,
  colors,
  targetBaseFontSizePx,
  wideCenterRem,
  fontStacks,
} from "../global-style";
import styled, { css } from "styled-components";
import { LayoutContext } from "./layout";
import Img, { FluidObject } from "gatsby-image";
import { useLocation } from "@reach/router";
import ReactResizeDetector from "react-resize-detector";
import { sortBy } from "lodash";
import { motion } from "framer-motion";
import { Link } from "gatsby";

const flushBlocks = ["Wp_CoreGroupBlock", "Wp_AcfEnergyAtlasBlock"];

const Container = styled.div<{ blocks: BlockFragment[]; allWide: boolean }>`
  /**
   * ...
   */

  padding-top: ${props =>
    flushBlocks.includes(props.blocks?.slice(1)[0]?.__typename)
      ? 0
      : `${2 * spaceRem}rem`};

  padding-bottom: ${props =>
    flushBlocks.includes(props.blocks?.slice(-1)[0]?.__typename)
      ? 0
      : `${2 * spaceRem}rem`};

  /**
   * ...
   */

  > * {
    box-sizing: content-box;
    margin-left: auto;
    margin-right: auto;
    max-width: ${narrowCenterRem}rem;
  }

  ${props =>
    props.allWide &&
    css`
      ${Container} > *,
      > * {
        max-width: ${wideCenterRem}rem;
      }
    `}

  max-width: none;

  /**
   * ...
   */

  > * {
    padding-left: ${0.5 * spaceRem}rem;
    padding-right: ${0.5 * spaceRem}rem;
  }

  padding-left: 0;
  padding-right: 0;

  /**
   * ...
   */

  > * + * {
    margin-top: ${0.5 * spaceRem}rem;
  }

  & + & {
    margin-top: 0;
  }

  /**
   * ...
   */

  a {
    text-decoration: underline;
    transition: all 0.2s ease;
  }

  a:hover {
    color: ${colors.mediumBlue};
  }
`;

export const Blocks: FC<{
  blocks: BlockFragment[];
  allWide: boolean;
}> = props => {
  const { remInPx } = useContext(LayoutContext);

  const location = useLocation();
  const currentLanguage = location.pathname.split("/").filter(v => v)[0] as
    | "nl"
    | "en";

  const Block: FC<{ block: BlockFragment }> = props => {
    const { block } = props;

    // core/paragraph
    if (
      block.__typename === "Wp_CoreParagraphBlock" &&
      block.attributes?.__typename === "Wp_CoreParagraphBlockAttributesV3"
    ) {
      return (
        <p dangerouslySetInnerHTML={{ __html: block.attributes.content }} />
      );
    }

    // core/heading
    if (block.__typename === "Wp_CoreHeadingBlock") {
      return (
        <div>
          {(block.attributes?.level === 1 && (
            <h1
              dangerouslySetInnerHTML={{
                __html: block.attributes?.content,
              }}
            />
          )) ||
            (block.attributes?.level === 2 && (
              <h2
                style={{
                  color: colors.mediumBlue,
                }}
                css={(() => {
                  const minFontSizeRem = 1.5;
                  const maxFontSizeRem = 2.25;
                  const vwRangeStartPx = 400;

                  return css`
                    font-size: ${(minFontSizeRem *
                      (remInPx || targetBaseFontSizePx)) /
                      (vwRangeStartPx / 100)}vw;

                    @media (max-width: ${vwRangeStartPx}px) {
                      font-size: ${minFontSizeRem}rem;
                    }

                    @media (min-width: ${vwRangeStartPx *
                        (maxFontSizeRem / minFontSizeRem)}px) {
                      font-size: ${maxFontSizeRem}rem;
                    }
                  `;
                })()}
                dangerouslySetInnerHTML={{
                  __html: block.attributes.content,
                }}
              />
            )) ||
            (block.attributes?.level === 3 && (
              <h3
                dangerouslySetInnerHTML={{
                  __html: block.attributes.content,
                }}
              />
            )) ||
            (block.attributes?.level === 4 && (
              <h4
                dangerouslySetInnerHTML={{
                  __html: block.attributes.content,
                }}
              />
            ))}
        </div>
      );
    }

    // core/image
    if (block.__typename === "Wp_CoreImageBlock") {
      return (
        <div
          style={{
            paddingTop: `${0.5 * spaceRem}rem`,
            paddingBottom: `${0.5 * spaceRem}rem`,
          }}
        >
          {block.attributes?.mediaItem?.mimeType === "image/svg+xml" ? (
            <img
              src={block.attributes?.mediaItem?.localFile?.publicURL as string}
              style={{ borderRadius: 2 }}
            />
          ) : (
            <Img
              style={{ paddingLeft: 0, paddingRight: 0, borderRadius: 2 }}
              imgStyle={{}}
              fluid={
                block.attributes?.mediaItem?.localFile?.childImageSharp
                  ?.fluid as FluidObject
              }
            />
          )}
        </div>
      );
    }

    // core/list
    if (block.__typename === "Wp_CoreListBlock") {
      return (
        <ul
          style={{
            listStyleType: "disc",
            paddingLeft: `${1 * spaceRem}rem`,
            boxSizing: "border-box",
          }}
          dangerouslySetInnerHTML={{
            __html: block.attributes?.values || "",
          }}
        />
      );
    }

    // core/group
    if (block.__typename === "Wp_CoreGroupBlock") {
      return (
        <Container
          blocks={block.innerBlocks}
          allWide={props.allWide}
          css={`
            :nth-child(odd) {
              background-color: ${colors.white};
            }

            :nth-child(even) {
              background-color: ${colors.lightestBlue};
            }
          `}
        >
          {block.innerBlocks?.map((block, i) => (
            <Block key={i} block={block as BlockFragment} />
          ))}
        </Container>
      );
    }

    // core/columns
    if (block.__typename === "Wp_CoreColumnsBlock") {
      return (
        <div style={{ overflow: "hidden" }}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              margin: `${-0.5 * spaceRem}rem`,
            }}
          >
            {block.innerBlocks?.map((column, i) => (
              <div
                key={i}
                style={{
                  margin: `${0.5 * spaceRem}rem`,
                  flexGrow: 1,
                  flexBasis: "20rem",
                }}
                css={`
                  > * + * {
                    margin-top: ${0.5 * spaceRem}rem;
                  }
                `}
              >
                {column?.innerBlocks?.map((block, i) => (
                  <Block key={i} block={block as BlockFragment} />
                ))}
              </div>
            ))}
          </div>
        </div>
      );
    }

    // acf/energy-atlas
    if (block.__typename === "Wp_AcfEnergyAtlasBlock") {
      return (
        <iframe
          style={{
            width: "100%",
            height: "80vw",
            maxHeight: 800,
            maxWidth: "none",
            padding: 0,
            display: "block",
          }}
          frameBorder="0"
          scrolling="no"
          src="https://northseaenergy.projectatlas.app/atlas/introduction?map=53.26425,2.47466,6.45,0,0"
        ></iframe>
      );
    }

    // acf/video
    if (block.__typename === "Wp_AcfVideoBlock") {
      return (
        <div style={{ maxWidth: `${wideCenterRem}rem` }}>
          <div
            style={{
              position: "relative",
              paddingBottom: "56.25%",
              height: 0,
              overflow: "hidden",
            }}
          >
            <iframe
              src={block.acf?.video_vimeo_url || ""}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
              }}
              frameBorder="0"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      );
    }

    // acf/downloads
    if (block.__typename === "Wp_AcfDownloadsBlock") {
      return (
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: `${0.75 * spaceRem}rem`,
            }}
          >
            <svg width={36.4} height={27.4} viewBox="0 0 36.4 27.4">
              <path
                d="M32.825 4.7h-13.5L15.484.859A2.25 2.25 0 0013.893.2H3.575A3.375 3.375 0 00.2 3.575v20.25A3.375 3.375 0 003.575 27.2h29.25a3.375 3.375 0 003.375-3.375V8.075A3.375 3.375 0 0032.825 4.7zM22.338 15.95L18.2 20.309l-4.137-4.359zm11.612 7.875a1.125 1.125 0 01-1.125 1.125H3.575a1.125 1.125 0 01-1.125-1.125V3.575A1.125 1.125 0 013.575 2.45h10.318l3.182 3.183V13.7h-3.7a1.894 1.894 0 00-1.758 1.176A2 2 0 0012 17.042l4.79 5.047a1.986 1.986 0 002.826 0l4.79-5.045a2 2 0 00.383-2.168 1.894 1.894 0 00-1.761-1.176h-3.7V6.95h13.5a1.125 1.125 0 011.125 1.125z"
                fill={colors.darkestBlue}
                stroke={colors.white}
                strokeWidth={0.4}
              />
            </svg>

            <h3 style={{ marginLeft: `${0.5 * spaceRem}rem`, fontWeight: 600 }}>
              Downloads
            </h3>
          </div>

          {block.acf?.downloads && (
            <ul>
              {block.acf.downloads.map((download, i) => (
                <li
                  key={i}
                  style={{
                    padding: `${0.5 * spaceRem}rem`,
                    boxShadow: "0 0 25px #E2F0F4",
                    marginBottom: `${0.5 * spaceRem}rem`,
                  }}
                >
                  <a
                    style={{ display: "flex", alignItems: "center" }}
                    href={download.downloads_file?.localFile?.publicURL}
                    target="_blank"
                  >
                    <svg
                      width={16.595}
                      height={22.131}
                      viewBox="0 0 16.595 22.131"
                      style={{
                        flexShrink: 0,
                        marginLeft: `${0.25 * spaceRem}rem`,
                      }}
                    >
                      <path
                        data-name="file-alt-light (4)"
                        d="M15.986 4.235L12.36.609A2.074 2.074 0 0010.895 0H2.074A2.081 2.081 0 000 2.079v17.978a2.075 2.075 0 002.074 2.074h12.447a2.075 2.075 0 002.079-2.074V5.7a2.085 2.085 0 00-.609-1.469zm-.977.981a.68.68 0 01.182.32h-4.128V1.409a.68.68 0 01.32.182zm-.488 15.532H2.074a.693.693 0 01-.691-.691V2.079a.693.693 0 01.691-.691h7.607v4.494a1.035 1.035 0 001.037 1.037h4.495v13.138a.693.693 0 01-.692.691zM12.446 10.2v.346a.52.52 0 01-.519.519h-7.26a.52.52 0 01-.519-.519V10.2a.52.52 0 01.519-.519h7.26a.52.52 0 01.519.519zm0 2.766v.346a.52.52 0 01-.519.519h-7.26a.52.52 0 01-.519-.519v-.346a.52.52 0 01.519-.519h7.26a.52.52 0 01.519.522zm0 2.766v.346a.52.52 0 01-.519.519h-7.26a.52.52 0 01-.519-.519v-.346a.52.52 0 01.519-.519h7.26a.52.52 0 01.519.522z"
                      />
                    </svg>

                    <span
                      style={{
                        marginLeft: `${0.5 * spaceRem}rem`,
                        marginRight: "auto",
                      }}
                    >
                      {download.downloads_title}
                    </span>

                    <button
                      style={{
                        marginLeft: `${0.5 * spaceRem}rem`,
                        flexShrink: 0,
                      }}
                    >
                      Download
                    </button>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    }

    // acf/people
    if (block.__typename === "Wp_AcfPeopleBlock") {
      return (
        <ReactResizeDetector
          handleWidth
          render={({ width }) => (
            <div
              style={{
                maxWidth: `${wideCenterRem}rem`,
                display: "grid",
                gridTemplateColumns: `repeat(auto-fill, minmax(${
                  width > 550 ? "500px" : "auto"
                }, 1fr))`,
                gridGap: `${spaceRem}rem`,
              }}
            >
              {block.attributes?.people?.edges
                ?.filter(edge =>
                  block.acf?.people_selection === "type"
                    ? edge?.node?.language?.slug === currentLanguage
                    : true
                )
                .map((edge, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                    }}
                  >
                    <div style={{ flexBasis: 0, flexGrow: 1, maxWidth: "40%" }}>
                      {edge?.node?.personCustomFields?.personPortrait?.localFile
                        ?.childImageSharp?.fluid && (
                        <Img
                          fluid={
                            edge.node.personCustomFields.personPortrait
                              .localFile.childImageSharp.fluid as FluidObject
                          }
                          style={{
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      )}
                    </div>
                    <div
                      style={{
                        flexBasis: 0,
                        flexGrow: 1,
                        flexShrink: 0,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <div style={{ padding: `${0.5 * spaceRem}rem` }}>
                        <p
                          style={{
                            fontFamily: fontStacks.poppins,
                            fontWeight: 600,
                            fontSize: "1.1rem",
                          }}
                          dangerouslySetInnerHTML={{
                            __html:
                              edge?.node?.personCustomFields?.personName || "",
                          }}
                        />

                        <p
                          style={{ fontSize: "0.9rem" }}
                          dangerouslySetInnerHTML={{
                            __html:
                              edge?.node?.personCustomFields?.personBio || "",
                          }}
                        />
                      </div>
                      <div
                        style={{
                          marginTop: "auto",
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          backgroundColor: colors.mediumBlue,
                          color: colors.white,
                          fontFamily: fontStacks.poppins,
                          fontWeight: 600,
                          textTransform: "uppercase",
                          fontSize: "0.75rem",
                          letterSpacing: 2.5,
                          paddingLeft: `${0.25 * spaceRem}rem`,
                          paddingRight: `${0.25 * spaceRem}rem`,
                        }}
                      >
                        {edge?.node?.personCustomFields?.personLinkedin && (
                          <a
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: `${0.5 * spaceRem}rem`,
                            }}
                            href={edge.node.personCustomFields.personLinkedin}
                            target="_blank"
                          >
                            <svg
                              width={14.859}
                              height={14.825}
                              viewBox="0 0 14.859 14.825"
                              style={{ position: "relative", top: -3 }}
                            >
                              <path
                                d="M.245 4.923h3.086v9.9H.249zM1.787 0A1.785 1.785 0 110 1.784 1.786 1.786 0 011.787 0"
                                fill={colors.white}
                              />
                              <path
                                d="M5.26 4.923h2.952v1.353h.041a3.237 3.237 0 012.914-1.6c3.116 0 3.692 2.05 3.692 4.717v5.432h-3.076V10.01c0-1.149-.024-2.627-1.6-2.627-1.6 0-1.847 1.25-1.847 2.542v4.9H5.26z"
                                fill={colors.white}
                              />
                            </svg>

                            <span
                              style={{
                                marginLeft: `${0.5 * spaceRem}rem`,
                                whiteSpace: "nowrap",
                              }}
                            >
                              LinkedIn
                            </span>
                          </a>
                        )}

                        {edge?.node?.personCustomFields?.personEmail && (
                          <a
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: `${0.5 * spaceRem}rem`,
                            }}
                            href={`mailto:${edge.node.personCustomFields.personEmail}`}
                          >
                            <svg
                              width={14.737}
                              height={14.737}
                              viewBox="0 0 14.737 14.737"
                              style={{ position: "relative", top: -2 }}
                            >
                              <path
                                d="M9.982 1.663C9.392 1.194 8.4.2 7.368.2s-2 .974-2.613 1.463A498.28 498.28 0 00.693 4.94 1.344 1.344 0 00.2 5.98v7.213a1.344 1.344 0 001.344 1.344h11.649a1.344 1.344 0 001.344-1.344V5.98a1.344 1.344 0 00-.493-1.04 498.28 498.28 0 00-4.062-3.277zm3.211 11.977H1.544a.448.448 0 01-.444-.447v-7.22a.448.448 0 01.165-.347c.736-.6 3.41-2.751 4.052-3.261C5.806 1.972 6.705 1.1 7.368 1.1s1.562.876 2.056 1.269c.642.51 3.316 2.66 4.052 3.261a.448.448 0 01.165.347v7.219a.448.448 0 01-.448.448zm-.245-6.057a.336.336 0 01-.05.473c-.639.516-1.544 1.239-2.917 2.328-.589.47-1.592 1.471-2.613 1.463s-2.039-1.005-2.613-1.463a338.17 338.17 0 01-2.916-2.327.336.336 0 01-.05-.473l.141-.174a.336.336 0 01.47-.05c.637.515 1.54 1.236 2.91 2.324.472.376 1.406 1.279 2.056 1.269.65.01 1.584-.893 2.056-1.269a301.022 301.022 0 002.913-2.324.336.336 0 01.472.05z"
                                fill={colors.white}
                                stroke={colors.white}
                                strokeWidth={0.4}
                              />
                            </svg>

                            <span
                              style={{
                                marginLeft: `${0.5 * spaceRem}rem`,
                                whiteSpace: "nowrap",
                              }}
                            >
                              Stuur e-mail
                            </span>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        />
      );
    }

    // acf/partners
    if (block.__typename === "Wp_AcfPartnersBlock") {
      return (
        <div
          style={{
            maxWidth: `${wideCenterRem}rem`,
            display: "grid",
            gridTemplateColumns: `repeat(auto-fill, minmax(200px, 1fr))`,
            gridGap: `${spaceRem}rem`,
          }}
        >
          {sortBy(
            block.attributes?.partners?.edges?.filter(
              edge => edge?.node?.language?.slug === currentLanguage
            ),
            edge => edge?.node?.partnerCustomFields?.partnerName
          ).map((edge, i) => (
            <a
              key={i}
              href={edge?.node?.partnerCustomFields?.partnerWebsite || ""}
              target="_blank"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: `${0.5 * spaceRem}rem`,
                boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                borderRadius: 3,
              }}
              css={`
                & > img {
                  filter: grayscale(1);
                }

                :hover > img {
                  filter: grayscale(0);
                }
              `}
            >
              <img
                style={{ width: 175, transition: "all .5s ease" }}
                src={
                  edge?.node?.partnerCustomFields?.partnerLogo?.localFile
                    ?.publicURL || ""
                }
              />
            </a>
          ))}
        </div>
      );
    }

    return (
      <div
        style={{
          paddingLeft: `${0.5 * spaceRem}rem`,
          paddingRight: `${0.5 * spaceRem}rem`,
        }}
      >
        Missing implementation: {props.block.__typename}
      </div>
    );
  };

  return (
    <Container blocks={props.blocks} allWide={props.allWide}>
      {props.blocks.map((block, i) => (
        <Block key={i} block={block} />
      ))}
    </Container>
  );
};
